import { type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { Button } from '../Button/Button';
import { Textarea } from '../Textarea/Textarea';

export interface RejectionPreset {
  readonly id: string;
  readonly label: string;
  readonly description?: string | undefined;
}

export interface RejectionReasonPickerProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'onChange'
> {
  readonly presets: ReadonlyArray<RejectionPreset>;
  readonly selectedId: string | null;
  readonly freeformText: string;
  readonly onChange: (selectedId: string | null, freeformText: string) => void;
  readonly onSubmit: () => void;
  /** Pre-rendered message preview displayed below the form. */
  readonly messagePreview?: string | undefined;
  /** Optional label/i18n overrides */
  readonly headline?: string | undefined;
  readonly freeformLabel?: string | undefined;
  readonly freeformPlaceholder?: string | undefined;
  readonly previewLabel?: string | undefined;
  readonly submitLabel?: string | undefined;
  readonly submitting?: boolean | undefined;
}

/**
 * Right-pane reject form — radio cards on top, freeform textarea below, and
 * a message-preview card showing the email/notification the user will get.
 *
 * Web-only — admin surface only.
 */
export function RejectionReasonPicker({
  presets,
  selectedId,
  freeformText,
  onChange,
  onSubmit,
  messagePreview,
  headline = 'Reject with reason',
  freeformLabel = 'Additional details',
  freeformPlaceholder = 'Add a short note explaining what we need.',
  previewLabel = 'Message preview',
  submitLabel = 'Send rejection',
  submitting = false,
  className,
  ...rest
}: RejectionReasonPickerProps) {
  return (
    <div className={cn('flex flex-col gap-4', className)} {...rest}>
      <h3 className="text-h3 font-bold text-ink">{headline}</h3>

      {/* Preset radio cards */}
      <fieldset className="flex flex-col gap-2">
        <legend className="sr-only">Choose a reason</legend>
        {presets.map((preset) => {
          const checked = selectedId === preset.id;
          return (
            <label
              key={preset.id}
              className={cn(
                'flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                checked
                  ? 'border-accent bg-accent/10'
                  : 'border-hairline-2 bg-surface hover:bg-surface-2',
              )}
            >
              <input
                type="radio"
                name="rejection-reason"
                value={preset.id}
                checked={checked}
                onChange={() => onChange(preset.id, freeformText)}
                className="mt-0.5 size-4 accent-current text-accent"
                aria-label={preset.label}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold text-ink">{preset.label}</span>
                {preset.description ? (
                  <span className="block text-[11.5px] text-ink-3 mt-0.5 leading-snug">
                    {preset.description}
                  </span>
                ) : null}
              </span>
            </label>
          );
        })}
      </fieldset>

      {/* Freeform textarea */}
      <div className="flex flex-col gap-1.5">
        <label className="text-[12px] font-semibold text-ink-2 font-mono uppercase tracking-wider">
          {freeformLabel}
        </label>
        <Textarea
          rows={4}
          maxLength={500}
          showCounter
          placeholder={freeformPlaceholder}
          value={freeformText}
          onChange={(e) => onChange(selectedId, e.target.value)}
        />
      </div>

      {/* Message preview */}
      {messagePreview ? (
        <div className="rounded-lg border border-hairline-2 bg-surface p-3">
          <p className="text-[11px] font-bold uppercase tracking-wider text-ink-3 font-mono mb-2">
            {previewLabel}
          </p>
          <p className="text-[13px] text-ink-2 leading-relaxed whitespace-pre-wrap">
            {messagePreview}
          </p>
        </div>
      ) : null}

      <div className="flex justify-end">
        <Button
          variant="destructive"
          onClick={onSubmit}
          disabled={!selectedId && !freeformText.trim()}
          loading={submitting}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

export default RejectionReasonPicker;
